import apiClient from './client'
import type { DropReason } from '@/constants/codeConstants'
import { resizeImageFile } from '@/utils/image'
import { isPhotoTooLarge, PHOTO_MAX_MESSAGE } from '@/constants/uploadRules'

/**
 * Member API
 * 회원 관리 API
 */

// ========================================
// Types
// ========================================

export type MemberGrade = '정회원' | '준회원' | '명예회원'
export type MemberStatus = 'ACTIVE' | 'PENDING' | 'SUSPENDED' | 'WITHDRAWN'

export interface Member {
  userSeq: number
  userName: string
  userNickname?: string
  userEmail: string
  userTel?: string
  memberGrade: MemberGrade
  memberStatus: MemberStatus
  joinDate: string
  lastActivityDate?: string
  profileImage?: string
  clubSeq?: number
  clubName?: string
}

export interface MemberListRequest {
  clubCode?: string
  clubSeq?: number
  page: number
  pageSize: number
  searchCondition?: string
  searchKeyword?: string
  memberGrade?: MemberGrade
  memberStatus?: MemberStatus
  sortOrder?: string
  roleFilter?: string
}

export interface MemberListResponse {
  items: Member[]
  totalCount: number
  totalPages: number
  currentPage: number
}

export interface MemberDetailResponse {
  userSeq: number
  userName: string
  userNickname?: string
  userEmail: string
  userTel?: string
  memberGrade: MemberGrade
  memberStatus: MemberStatus
  joinDate: string
  lastActivityDate?: string
  profileImage?: string
  clubSeq: number
  clubName: string
  // 추가 정보
  participationCount?: number
  totalFee?: number
  currentPoint?: number
}

export interface MemberUpdateRequest {
  userNickname?: string
  memberGrade?: MemberGrade
  memberStatus?: MemberStatus
  profileImage?: string
}

export interface MemberFeeHistory {
  feeSeq: number
  feeType: 'MEMBERSHIP' | 'PARTICIPATION' | 'OTHER'
  amount: number
  paidDate: string
  description: string
  paymentMethod?: string
  status: 'PAID' | 'PENDING' | 'CANCELLED'
}

export interface MemberFeeListRequest {
  userSeq: number
  page: number
  pageSize: number
  feeType?: string
  startDate?: string
  endDate?: string
}

export interface MemberFeeListResponse {
  items: MemberFeeHistory[]
  totalCount: number
  totalPages: number
  totalAmount: number
}

// ========================================
// API Client
// ========================================

export const memberApi = {
  /**
   * 회원 목록 조회
   * @param params 검색 조건
   * @returns 회원 목록
   */
  getMembers: async (params: MemberListRequest) => {
    return apiClient.get<MemberListResponse>('/api/member/list', { params })
  },

  /**
   * 회원 상세 조회
   * @param clubCode 동호회 인코딩 코드
   * @param clubMemberSeq
   */
  getMember: async (clubCode: string, clubMemberSeq: number) => {
    return apiClient.get<MemberDetailResponse>(`/api/club/${clubCode}/members/${clubMemberSeq}`)
  },

  /**
   * 회원 정보 수정
   * @param userSeq 회원 일련번호
   * @param data 수정할 데이터
   */

  /**
   *
   * @param clubSeq 클럽 일련번호
   * @param clubMemberSeq 클럽 멤버 일련번호
   * @param data 수정할 데이터
   */
  updateMember: async (clubCode: string, clubMemberSeq: number, data: { memberGrade?: any; adjustedHandicap?: any }) => {
    return apiClient.put(`/api/club/${clubCode}/members/${clubMemberSeq}`, data)
  },

  /**
   * 회원 탈퇴
   * @param userSeq 회원 일련번호
   */
  deleteMember: async (userSeq: number) => {
    return apiClient.delete(`/api/member/${userSeq}`)
  },

  /**
   * 회원 회비 내역 조회
   * @param params 검색 조건
   * @returns 회비 내역
   */
  getMemberFees: async (params: MemberFeeListRequest) => {
    return apiClient.get<MemberFeeListResponse>('/api/member/fee/list', { params })
  },

  /**
   * 동호회별 회원 통계
   * @param clubCode 동호회 인코딩 코드
   */
  getMemberStatistics: async (clubCode: string) => {
    return apiClient.get<{
      totalMembers: number
      regularMembers: number
      associateMembers: number
      honoraryMembers: number
      activeMembers: number
      newMembersThisMonth: number
    }>(`/api/member/statistics/${clubCode}`)
  },

  /**
   * 회원 강제탈퇴 (운영진 전용)
   * @param clubCode 동호회 인코딩 코드
   * @param userSeq 회원 일련번호
   */
  expelMember: async (clubCode: string, userSeq: number) => {
    return apiClient.post<{
      code: number
      message: string
    }>(`/api/club/${clubCode}/members/${userSeq}/expel`)
  },

  /**
   * 현재 로그인한 사용자 정보 조회
   */
  getMyInfo: async () => {
    return apiClient.get('/api/member/me')
  },

  /**
   * 현재 로그인한 사용자 정보 수정
   * @param data 수정할 필드 (userNick, birthday, gender, handicap, golfCareer, favoriteArea, jobNm, workplace, userPosition)
   */
  updateMyInfo: async (data: Record<string, unknown>) => {
    return apiClient.put('/api/member/me', data)
  },

  /**
   * 현재 로그인한 사용자 프로필 사진 업로드
   * @param file 이미지 파일
   *
   * ⚠️ 2026-09-29 발주 슬라이드 5 — 휴대폰 사진이 옆으로 누워 보였다.
   *    휴대폰 카메라는 픽셀을 돌리지 않고 «회전 정보(EXIF Orientation)» 만 붙여 저장하는데,
   *    서버는 그 정보를 반영하지 않는다(썸네일·원본 모두). 원본을 그대로 올리던 이 경로만 누웠다.
   *    다른 업로드(fileApi.uploadTemp)처럼 canvas 로 다시 그려 올린다 — 브라우저가 회전 정보를 적용해
   *    «똑바로 선 픽셀» 로 저장되고, 최대 1200px·JPEG 로 줄어든다.
   *    이미지가 아니거나 다시 그리기에 실패하면 resizeImageFile 이 경고를 남기고 원본을 돌려준다(종전 동작).
   */
  uploadMyProfilePhoto: async (file: File) => {
    // 2026-10-02 10/1 §7-E3 — 9/29 «장당 5MB» 정책(다른 사진 업로드와 같다). 서버도 같은 한도로 막는다
    if (isPhotoTooLarge(file)) throw new Error(PHOTO_MAX_MESSAGE)
    const processedFile = await resizeImageFile(file)
    const formData = new FormData()
    formData.append('file', processedFile)
    return apiClient.post('/api/member/me/profile-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  /**
   * 현재 로그인한 사용자 프로필 사진 삭제
   */
  deleteMyProfilePhoto: async () => {
    return apiClient.delete('/api/member/me/profile-photo')
  },

  /**
   * 비밀번호 변경
   * @param currentPassword 현재 비밀번호
   * @param newPassword 새 비밀번호
   */
  changePassword: async (currentPassword: string, newPassword: string) => {
    return apiClient.put<{
      code: number
      message: string
    }>('/api/member/me/password', { currentPassword, newPassword })
  },

  /**
   * 회원 탈퇴 전 확인 — 막는 사유 · 소멸되는 적립금·골총캐쉬 · 동호회 · 참가 예정 행사 · 진행 중 주문·프리미엄
   * (2026-10-01 발주 9/30 슬라이드 12·13 — 종전엔 누른 뒤에야 거절 문구가 떴다)
   */
  withdrawalCheck: async () => {
    return apiClient.get<{
      code: number
      message: string
      data: WithdrawalCheck
    }>('/api/member/me/withdrawal-check')
  },

  /**
   * 회원 탈퇴 — 사유 필수(DROP_REASON), 기타는 내용 필수. 종전엔 사유를 보내도 서버가 버렸다.
   */
  withdraw: async (reasonCode: DropReason, reasonDetail: string) => {
    return apiClient.delete<{
      code: number
      message: string
    }>('/api/member/me', { data: { reasonCode, reasonDetail } })
  },
}

/** GET /api/member/me/withdrawal-check — core FrontUserService.withdrawalCheck */
export interface WithdrawalCheck {
  /** 탈퇴를 막는 사유(운영진 역할 · 환불 가능한 골총캐쉬 · 처리 중인 환불 신청) — 비어 있어야 탈퇴할 수 있다 */
  blocks: string[]
  /** 골총캐쉬(유상) 구분 — refundable 은 지금 환불할 수 있는 잔액(최소 금액 이상이면 blocks 에 들어간다) */
  cash: { refundable: number; cardWaiting: number; iap: number; admin: number; other: number; minRefund: number }
  /** 소멸되는 적립금 */
  reward: number
  /** 탈퇴되는 동호회 이름 */
  clubs: string[]
  /** 자동 취소되는 참가 예정 행사 */
  upcomingEvents: { eventName: string; eventDate: string; clubName: string; status: string }[]
  /** 진행 중 쇼핑몰 주문 수 — 2026-10-07 10/7본 15번 장부터 1건 이상이면 blocks 에 들어간다(탈퇴 불가) */
  openOrders: number
  /** 진행 중 프리미엄 서비스 신청 수 — 위와 같다 */
  openPremium: number
  /** 진행 중 쇼핑몰 주문 건별(10/7본 16번 장 «구매 중인 서비스») — status 는 ORDER_STATUS 코드 · regDate yyyyMMddHHmmss */
  openOrderList?: { orderSeq: number; orderNo: string; name: string | null; itemCount: number; status: string; regDate: string }[]
  /** 진행 중 프리미엄 서비스 신청 건별 — status 는 신청 상태 코드(APPLIED·CONSULT_WAIT …) */
  openPremiumList?: { applySeq: number; name: string; status: string; regDate: string }[]
}

export default memberApi
